import Component from '../core/Component';
import { KV } from '../core/Constant';
import { PlayerEvent } from '../PlayerEvents';
import { createElement, removeFromParent } from '../utils/Dom';
import { systemProbe } from '../utils/util';
import log from 'loglevel';

const VIDEO_ID_SUFFIX = '-vhall-video';
/**
 * 内核播放器的封装基类
 *
 * @class PlayerProxy
 * @extends {Component}
 */
class PlayerProxy extends Component {
  constructor(cfg = {}) {
    super();
    this._option = cfg;
    this.browserType = systemProbe().browser;
    this.platform = systemProbe().platform;
    this._volume = 1;
    this._lastMuted = false;
    this.videoID = '';

    this._src = '';
    this._isLive = false;
    this._isPlaying = false;
    this._isPlayingBefore = undefined;
    this._manualPaused = false;
    this._playedEver = false;
    this._setSrcCount = 0;
    this.video = null;
    this._store = null;
    //原生video事件，见https://www.w3school.com.cn/tags/html_ref_audio_video_dom.asp
    this.videoEventList = {
      canplay: this.__canplay.bind(this),
      canplaythrough: this.__canplaythrough.bind(this),
      /** 将要开始播放时触发 */
      play: this.__play.bind(this),
      /** 已开始播放时触发 */
      playing: this.__playing.bind(this),
      /** 开始加载新的数据，每加载一次，执行一次 */
      progress: this.__progress.bind(this),
      /** 播放出错 */
      error: this.__error.bind(this),
      /** 视频时间戳更新触发事件 */
      timeupdate: this.__timeupdate.bind(this),
      /** 开始加载音视频时 */
      loadstart: this.__loadstart.bind(this),
      /** 视频设置src后，加载元数据后，派发的事件，音频/视频的元数据包括：时长、尺寸（仅视频）以及文本轨道。 */
      loadedmetadata: this.__loadedmetadata.bind(this),
      /** 事件在媒体当前播放位置的视频帧（通常是第一帧）加载完成后触发 */
      loadeddata: this.__loadeddata.bind(this),
      /** 开始seek时 */
      seeking: this.__seeking.bind(this),
      /** seek完成后，触发的事件 */
      seeked: this.__seeked.bind(this),
      /** 当没有buffer开始播放的时候，派发waiting事件，也就是说，卡顿了 */
      waiting: this.__waiting.bind(this),
      /** 播放速率发生变更的时候，派发的事件 */
      ratechange: this.__ratechange.bind(this),
      /** 声音发生改变的时候，派发的事件 */
      volumechange: this.__volumechange.bind(this),
      /** 暂停 */
      pause: this.__pause.bind(this),
      /** 当音频/视频的加载已放弃时触发。(预留)**/
      abort: e => {},
      /** 当浏览器尝试获取媒体数据，但数据不可用时触发。**/
      stalled: this.__stalled.bind(this)
    };
    // 自计算的当前播放时间，
    // 目前发现非MSE模式下，腾讯系安卓直播时无法获取当前时间。
    this.calcTimeStamp = 0;

    this.reset();
  }

  /**
   * 创建video标签
   * todo: config参数并不需要
   */
  initVideo(config = {}) {
    this._isLive = config['isLive'];
    this.isOpenTimeshift = config['isOpenTimeshift'];

    this._root = document.getElementById(config['id']);

    // 初始化video标签
    this.videoID = config['id'] + VIDEO_ID_SUFFIX;
    if (document.getElementById(this.videoID)) {
      log.info('video标签已存在');
      this.video = document.getElementById(this.videoID);
      return;
    }

    let x5cfg = {};
    // 让视频在小窗内播放,禁止播放时自动全屏（ios9 对Safari无效），iphone上是默认全屏播放。
    x5cfg['playsinline'] = true;
    x5cfg['webkit-playsinline'] = true;
    x5cfg['x5-playsinline'] = true;
    x5cfg['mtt-playsinline'] = true;
    x5cfg['t7-video-player-type'] = 'inline'; // 解决android UC浏览器播放时自动全屏问题
    // x5cfg['x-webkit-airplay'] = 'allow';
    // x5cfg['x5-video-player-type'] = 'h5';
    // x5cfg['x5-video-player-fullscreen'] = true;
    // x5cfg['x5-video-orientation'] = 'portrait';
    // let poster = config['poster'] && this.platform === 'pc'
    //   ? {
    //     poster: config['poster']
    //   }
    //   : {};
    this.video = createElement(
      'video',
      {
        id: this.videoID,
        controls: false,
        muted: config.muted
      },
      Object.assign(
        {
          width: '100%',
          height: '100%',
          crossOrigin: 'anonymous',
          'z-index': 0
        },
        x5cfg
        // poster
      )
    );
    const styleOption = config['videoStyleOption'];
    if (styleOption) {
      if (styleOption['objectFit']) {
        this.video.style.objectFit = styleOption['objectFit'];
      }
      // todo: may be more css style
    }

    const parent = this._root;
    parent.appendChild(this.video);
    parent.style.position = 'relative';

    this._lastMuted = this.video.muted || false;
  }

  /**
   * 添加字幕，参考https://developer.mozilla.org/en-US/docs/Web/HTML/Element/track
   */
  appendSubtitle(trackInfo, auto) {
    let oldTrack = this.video.querySelector('track[kind="subtitles"]');
    if (oldTrack) {
      this.video.removeChild(oldTrack);
    }
    const track = document.createElement('track');
    track.kind = 'subtitles';
    track.src = trackInfo['innerUrl'];
    track.srclang = trackInfo['lang'];
    track.label = trackInfo['lang'];
    if (auto) {
      track.default = trackInfo['isDefault'];
    }
    this.video.appendChild(track);
  }

  /**
   * 监听原生video事件
   */
  initEvents() {
    this._initOriginalEvents();
  }
  _initOriginalEvents() {
    if (!this.getIsLive()) {
      Object.assign(this.videoEventList, {
        /** 播放完毕执行的事件 */
        ended: this.__ended.bind(this)
      });
    }

    // 循环添加事件监听
    Object.keys(this.videoEventList).forEach(key => {
      this.video.addEventListener(key, this.videoEventList[key]);
    });
  }

  /**
   * 开始播放
   *
   * @memberof PlayerProxy
   */
  play() {
    if (this.video) {
      this.emit2All(PlayerEvent.TOUCH_PLAY);
      this.video.focus();
      this._manualPaused = false;
      return this.video.play();
    }
  }

  /**
   * 暂停当前正在播放的视频
   *
   * @memberof PlayerProxy
   */
  pause() {
    if (this.video) {
      this.video.pause();
      this._manualPaused = true;
    }
  }

  /**
   * 获取下载大小，指的是不论seek还是自然下载的总和
   * 针对于HLS，仅统计了TS大小
   * 针对于FLV, 统计flv流文件大小
   * 针对于原生MP4模拟计算出来的，如：一个MP4文件大小为100M，总时长为100分钟，则认为每分钟大小约为1M。
   *       通过video 的buffer 计算时间缓冲区间，然后累加获取
   *
   * @returns
   * @memberof PlayerProxy
   */
  get downloadSize() {
    return this.getStore().getKV(KV.DownloadSize) || -1;
  }

  /**
   * 查看当前视频正在处于暂停状态
   *
   * @readonly
   * @memberof PlayerProxy
   */
  getIsPaused() {
    if (!this.video) return undefined;
    return this.video.paused && this._isPlaying === false;
  }

  /**
   * Gets a collection of buffered time ranges.
   * 获取当前 video 的buffered ranges
   *
   * @readonly
   * @memberof PlayerProxy
   */
  getBuffered() {
    return this.video.buffered;
  }

  /**
   * 获取当前视频播放的时间，单位：秒
   *
   * @memberof PlayerProxy
   */
  getCurrentTime() {
    if (!this._isLive && this.video.currentTime === 0) {
      return 0;
    } else {
      // return this.video.currentTime || this.calcTimeStamp; // resolve => 部分浏览器（app内嵌自定义内核居多）在播放器直播的时候currentTime一直返回0
      let t = this.video.currentTime || this.calcTimeStamp;
      return Number.isFinite(t) ? t : 0;
    }
  }

  /**
   * 设置当前视频的播放时间，单位：秒
   *
   * @memberof PlayerProxy
   */
  setCurrentTime(t) {
    if (this.browserType === 'ie' && t === 0) {
      return;
    }
    this.video.currentTime = t;
    this.emit2All(PlayerEvent.CURRENT_TIME_CHANGED, t);
  }

  /**
   * 获取直播的时移时间 单位：秒
   *
   * @memberof PlayerProxy
   */
  getShiftStartTime() {
    return this.shiftStartTime;
  }

  /**
   * 设置直播时移时间
   *
   * @memberof PlayerProxy
   */
  handleTimeshiftPlay(opts) {
    this.emit2All(PlayerEvent.LIVE_TIMESHIFT, opts);
  }

  /**
   * 点播服务中，获取视频的总时长
   *
   * @readonly
   * @memberof PlayerProxy
   */
  getDuration() {
    return this.video.duration;
  }

  /**
   * 点播服务中，当视频播放完毕后是否进行循环播放
   *
   * @memberof PlayerProxy
   */
  setLoop(v) {
    let oldValue = this.video.loop;
    this.video.loop = v;
    let newValue = v;
    const e = { oldValue, newValue };
    this.emit2All(PlayerEvent.LOOP_CHANGED, e);
  }

  getLoop() {
    return this.video.loop;
  }

  /**
   * 获取当前视频播放器是否静音
   *
   * @memberof PlayerProxy
   */
  getMuted() {
    return this.video.muted || this.volume === 0;
  }

  /**
   * 设置当前video播放源是否为静音
   *
   * @memberof PlayerProxy
   */
  setMuted(b) {
    let oldValue = this.video.muted;
    this.video.muted = b;
    let newValue = b;
    const e = { oldValue, newValue };
    this.emit2All(PlayerEvent.MUTED_CHANGED, e);
  }

  /**
   * 获取当前元素网络状态
   * Gets the current network activity for the element.
   *
   * @readonly
   * @memberof PlayerProxy
   */
  getNetworkState() {
    return this.video.networkState;
  }

  /**
   * 点播服务中设置倍速
   *
   * @memberof PlayerProxy
   */
  setPlaybackRate(v) {
    v = +v;
    if (this.video.playbackRate !== v) {
      this.video.playbackRate = v;
    }
  }

  /**
   * 获取当前视频的倍速值
   *
   * @memberof PlayerProxy
   */
  getPlaybackRate() {
    return this.video.playbackRate;
  }

  /**
   * Gets TimeRanges for the current media resource that has been played.
   *
   * @readonly
   * @memberof PlayerProxy
   */
  getPlayed() {
    return this.video.played;
  }

  /**
   * 获取当前视频播放器的准备状态值
   *
   * @readonly
   * @memberof PlayerProxy
   * @returns [0,1,2,3,4] // 0:没有任何信息，1：拥有元数据，2：当前位置播放数据可用，但是下一帧数据不够用，3：当前及下一帧数据可用，4，数据足够多，可以播放了
   */
  getReadyState() {
    return this.video.readyState;
  }

  getSrc() {
    return this._src;
  }

  setSrc(url) {
    // 连续切线时，记录最开始的播放状态
    if (this._isPlayingBefore === undefined) {
      this._isPlayingBefore = this._isPlaying;
      this._isPlaying = false;
    }

    const e = {
      oldValue: this._src,
      newValue: url
    };
    this.emit2All(PlayerEvent.SRC_CHANGED, e);
    this.video.src = url;
    this._src = url;
    this.getStore().setKV(KV.URL, url);
    this._setSrcCount += 1;
  }

  setSrcObject(url) {
    if (this._isPlayingBefore === undefined) {
      this._isPlayingBefore = this._isPlaying;
      this._isPlaying = false;
    }

    const e = {
      oldValue: this._src,
      newValue: url
    };
    this.emit2All(PlayerEvent.SRC_CHANGED, e);
    this.video.srcObject = url;
    this.getStore().setKV(KV.URL, url);
    this._setSrcCount += 1;
  }

  /**
   * 设置音量
   *
   * @memberof PlayerProxy
   */
  getVolume() {
    return this._volume;
  }

  setVolume(v) {
    v = +v;

    if (v > 1 || v < 0) {
      log.warn(`volume value range should be between 0 to 1, now you set ${v}`);
      v = Math.min(Math.max(0, v), 1);
    }

    if (this._volume !== v) {
      this._volume = v;
      this.video.volume = this._volume;
    }

    this.setMuted(this._volume === 0);
  }

  getControls() {
    return this.video.controls;
  }

  setControls(val) {
    this.video.controls = val;
  }

  /**
   * 获取当前seek状态，是否跳转中
   *
   * @readonly
   * @memberof PlayerProxy
   * @returns true跳转中， false 跳转完成
   */
  getSeeking() {
    return this.video.seeking;
  }

  /**
   * 返回一个TimeRanges, 当前可跳转的时间区域
   * Returns a TimeRanges object that represents the ranges of the current media resource that can be seeked.
   *
   * @readonly
   * @memberof PlayerProxy
   * @returns 返回一个TimeRanges
   */
  getSeekable() {
    return this.video.seekable;
  }

  /**
   * 是否是直播状态
   *
   * @readonly
   * @memberof PlayerProxy
   * @returns 直播 true, 其他：false
   */
  getIsLive() {
    return this._isLive;
  }

  /**
   * 获取预估网速
   *
   * @readonly
   * @memberof PlayerProxy
   */
  getEstimateNetSpeed() {
    return 0;
  }

  getRoot() {
    return this._root;
  }

  getOwner() {
    return this._owner;
  }

  setStore(store) {
    this._store = store;
  }

  getStore() {
    return this._store;
  }

  emit2All(act, data) {
    // this.emit(act, data)
    this.getOwner() && this.getOwner().emit(act, data);
  }

  // 自动播放问题：
  // ios微信: 用nativePlayer播放，没有任何事件。
  // ios的浏览器等: 用nativePlayer播放,只有PROGRESS、LOADEDMETADATA事件。
  // 参考: https://blog.csdn.net/weixin_54443444/article/details/114730171
  // 参考: https://developer.apple.com/library/archive/documentation/AudioVideo/Conceptual/Using_HTML5_Audio_Video/AudioandVideoTagBasics/AudioandVideoTagBasics.html
  __canplay(e) {
    const isAutoplay = this._owner._config.autoplay || false;
    log.info(`video -> canplay -> autoplay=${isAutoplay}`, e);
    this.emit2All(PlayerEvent.CANPLAY);

    // 1、首次初始化且开启自动播放配置时，有推流，则处理自动播放
    // 2、首次初始化且开启自动播放配置时，由于主播未推流，播放器尝试切线，当推流成功后，仍需要处理自动播放
    // 3、直播播放过程中发生source_ended并触发内核级pause状态后，即时切线成功且有推流，也是暂停状态不继续播放，需干预自动播放
    // 4、todo: 其他情况
    // todo: 条件代码优化
    if (
      (!this._manualPaused && !this._isPlaying && isAutoplay) ||
      (!this._manualPaused && !this._isPlaying && this._playedEver)
    ) {
      log.info('尝试自动播放');
      if (this.browserType === 'wechat') {
        log.info('微信环境下尝试自动播放');
        const autoPlay = () => {
          WeixinJSBridge.invoke('getNetworkType', {}, res => {
            this.play()
              .then(() => {
                log.info('====>微信环境自动播放成功');
                this.emit2All(PlayerEvent.AUTOPLAY_SUCCESS, { isMuted: this.video.muted });
              })
              .catch(error => {
                log.info('====>微信环境自动播放失败');
                this.emit2All(PlayerEvent.AUTOPLAY_FAILED, { isMuted: this.video.muted });
              });
          });
        };

        if (WeixinJSBridge) {
          autoPlay();
        } else {
          document.addEventListener('WeixinJSBridgeReady', autoPlay);
        }

        return;
      } else {
        // 兼容性处理，chrome50+ play()方法才返回promise
        const playPromise = this.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              log.info('====>自动播放成功');
              this.emit2All(PlayerEvent.AUTOPLAY_SUCCESS, { isMuted: this.video.muted });
            })
            .catch(() => {
              if (!this.video.muted && this._option.autoplayMuteAttempt) {
                // 自动播放失败，设置静音后重试播放
                log.info('====>自动播放失败，设置静音后重试播放');
                this.setMuted(true);
                this.play()
                  .then(() => {
                    log.info('====>设置静音后自动播放成功，并触发 AUTOPLAY_SUCCESS 事件');
                    this.emit2All(PlayerEvent.AUTOPLAY_SUCCESS, { isMuted: this.video.muted });
                  })
                  .catch(error => {
                    log.info('====>设置静音后自动播放依然失败');
                    this.emit2All(PlayerEvent.AUTOPLAY_FAILED, { isMuted: this.video.muted });
                    // 静音还原回去，让用户手动点击时有声音
                    this.setMuted(false);
                  });
              } else {
                log.info('====>自动播放失败0');
                this.emit2All(PlayerEvent.AUTOPLAY_FAILED, { isMuted: this.video.muted });
              }
            });
        }
      }
    }
  }

  __canplaythrough(e) {
    log.info(`video -> canplaythrough`, e);
  }

  // video的play事件
  __play(e) {
    log.info('video -> play: ', e);
    this._isPlaying = true;
    this._isPlayingBefore = undefined;
    this._playedEver = true;
    this.emit2All(PlayerEvent.PLAY);
  }

  // video的playing事件
  // todo：如何利用？ 参考https://cloud.tencent.com/document/product/881/30820#.E4.BA.8B.E4.BB.B6
  __playing(e) {
    log.info('video -> playing: ', e);
    this._playedEver = true;
    this._isPlaying = true;
    this.emit2All(PlayerEvent.PLAYING);
  }

  // video的pause事件
  __pause(e) {
    log.info('video -> pause: ', e);
    this._isPlaying = false;
    this.emit2All(PlayerEvent.PAUSE);
  }

  __progress(e) {
    // log.info('video -> progress: ', e);
    this.emit2All(PlayerEvent.PROGRESS, e);
  }

  __error(e) {
    log.warn('video -> error: ', e);
    this.emit2All(PlayerEvent.ERROR, e);
  }

  __stalled(e) {
    log.warn('video -> stalled: ', e);
    this.emit2All(PlayerEvent.STALLED, e);
  }

  __timeupdate(e) {
    // log.debug('video -> timeupdate: ', e.target.currentTime);
    this.calcTimeStamp = e.timeStamp / 1000;
    // 每大于500ms 派发一次事件
    let now = Date.now();
    if (!this._lastEmitTimeupdate) {
      this._lastEmitTimeupdate = Date.now();
    }
    if (now - this._lastEmitTimeupdate > 100 && !isNaN(this.getDuration())) {
      // log.info( `onTimeupdate currentTime = ${this.getCurrentTime()}`);
      this._lastEmitTimeupdate = now;
      // 腾讯时移
      if (this._isLive && this.isOpenTimeshift && this._owner._config.type === 'hls' && this.getCdnName() === 'tx') {
        // 当前自然时间
        const nowDate = Date.now();
        if (!this.progressStartTime) {
          return;
        }
        // 进度条长度
        const progressDuration = nowDate / 1000 - this.progressStartTime;
        if (!progressDuration) {
          return;
        }
        e.progressStartTime = this.progressStartTime;
        e.progressDuration = progressDuration;

        // 已播长度 playedDuration
        // 当前播放时间戳 playedDate
        e.playedDuration = nowDate / 1000 - this.delay - this.progressStartTime;
        e.playedDate = (nowDate / 1000 - this.delay) * 1000;

        e.now = nowDate;
      }

      // 点播换清晰度后时间会重置为0再seek，时间回退时不派发事件
      if (!this._isLive && this.vodPlayedTime && this.getCurrentTime() < this.vodPlayedTime) {
        return;
      }
      // 直播时发起端与观看端的延时
      if (this._isLive && this._owner.currentDefinition) {
        const buffered = e.target.buffered;
        const currentTime = e.target.currentTime;
        const bufferedLen = buffered.length;
        const buffer = bufferedLen >= 1 ? buffered.end(bufferedLen - 1) : 0;
        // 计算时差
        let delay = buffer - currentTime;
        // 为避免部分手机老旧浏览器兼容性问题，导致计算错误。故加判断2020.03.12
        delay = delay < 120 ? delay : 10;
        // 链路延时+1.5s
        delay += 1.5;
        if (this._owner.currentDefinition.def !== 'same') {
          // 转码延时+2s，这有个坑。如果转码目标分辨率与原画分辨率一致，则后端转码直接拷贝无延时。但目前播放器无法判断。
          delay += 2;
        }
        // 避免传入负值
        delay = delay < 0 ? 0 : delay;
        e.delay = delay;
      }
      this.emit2All(PlayerEvent.TIMEUPDATE, e);
      this.vodPlayedTime = undefined;
    }
  }

  getCdnName() {
    return this.cdnName;
  }

  __ended(e) {
    log.info('video -> ended: ', e);
    this.emit2All(PlayerEvent.ENDED);
  }

  __loadstart(e) {
    log.info('video -> loadstart', e);
    this.emit2All(PlayerEvent.LOADSTART, e);
  }

  __loadedmetadata(e) {
    log.info('video -> loadedmetadata', e);
    this.emit2All(PlayerEvent.LOADEDMETADATA, e);
    // 在播放状态下切线需手动播放
    if (this._isPlayingBefore && !this._isPlaying) {
      log.info('更换地址重新拉流, 手动播放');
      this.play();
    }
  }

  __loadeddata(e) {
    log.info('video -> loadeddata', e);
    this.emit2All(PlayerEvent.LOADED, e);
  }

  __seeking(e) {
    log.info('video -> seeking', e);
    this.emit2All(PlayerEvent.SEEKING, e);
  }

  __seeked(e) {
    log.info('video -> seeked', e);
    this.emit2All(PlayerEvent.SEEKED, e);
  }

  __waiting(e) {
    log.info('video -> waiting', e);
    this.emit2All(PlayerEvent.WAITING, e);
  }

  __ratechange(e) {
    log.info('video -> ratechange', e);
    let rate = this.video.playbackRate;
    this.emit2All(PlayerEvent.RATECHANGE, rate);
  }

  __volumechange(e) {
    log.info('video -> volumechange', e);
    if (this._lastMuted !== e.target.muted) {
      log.info('触发静音事件变化事件');
      this.emit2All(PlayerEvent.MUTED_CHANGED, {
        oldValue: this._lastMuted,
        newValue: e.target.muted
      });
      this._lastMuted = e.target.muted;
    }
    this.emit2All(PlayerEvent.VOLUMECHANGE, e);
  }

  reset() {
    this._lastEmitTimeupdate = Date.now();
  }

  destroy() {
    super.destroy();
    window.peer5 && window.peer5.destroy();
    this._volume = 1;
    this._src = '';
    this._isLive = false;
    this._isPlaying = false;
    // 是否处于“人为暂停状态”
    this._manualPaused = false;
    // 是否曾经播放过
    this._playedEver = false;
    this.getStore().reset();
    this.reset();

    if (this.video) {
      Object.keys(this.videoEventList).forEach(key => {
        this.video.removeEventListener(key, this.videoEventList[key]);
      });

      removeFromParent(this.video);
      this.video = null;
    }
  }

  //=================================================================================
  //=================================================================================
  //以下：未使用或不再使用，择机清理
  //以下：未使用或不再使用，择机清理
  /**
   * 设置视频是否允许跨域播放
   *
   * @memberof PlayerProxy
   */
  getCrossOrigin() {
    return this.video.crossOrigin;
  }
  setCrossOrigin(v) {
    this.video.crossOrigin = v;
  }
  /**
   * 判断某个时间是否已经缓存了
   *
   * @param {*} time 秒为单位
   * @returns 返回是否已经缓存该时刻
   * @memberof PlayerProxy
   */
  timeInBuffer(time) {
    let result = false;
    if (this.buffered.length === 0) {
      return result;
    }

    time = +time;
    let len = this.buffered.length;
    for (let i = 0; i < len; i += 1) {
      if (time > this.buffered.end(i)) {
        continue;
      }

      if (time > this.buffered.start(i)) {
        result = true;
      }
    }
    return result;
  }
  /**
   * 设置自动播放，移动端实现自动播放同时需要将 muted 属性设置为true
   *
   * @memberof PlayerProxy
   */
  setAutoplay(v) {
    this.video.autoplay = v;
  }
  getAutoplay() {
    return this.video.autoplay;
  }
  /**
   * 设置默认是否为静音
   *
   * @memberof PlayerProxy
   */
  setDefaultMuted(v) {
    this.video.defaultMuted = v;
  }
  getDefaultMuted() {
    return this.video.defaultMuted;
  }
  /**
   * 是否播放完毕
   *
   * @readonly
   * @memberof PlayerProxy
   */
  getEnded() {
    return this.video.ended;
  }
  /**
   * 返回以秒为单位的预加载数据长度
   * Gets or sets the current playback position, in seconds.
   *
   * @readonly
   * @memberof PlayerProxy
   */
  getPreload() {
    return this.video.preload;
  }
  setPreload(t) {
    this.video.preload = t;
  }
}

export default PlayerProxy;
